import { DropdownChevron } from '@components'
import cn from '@integrations/classnames'
import type { DateInputHeaderProps } from '@integrations/core'
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from '@integrations/react'
import { tr } from 'mmfintech-commons'
import styles from './CalendarHeader.module.scss'

interface Props extends DateInputHeaderProps {
  minDate: Date // first day the calendar accepts
  maxDate: Date // last day the calendar accepts
  locale?: string // language code for the month names
}

type Panel = 'year' | 'month'

/**
 * Header of a date picker's calendar: month arrows, and month / year buttons that open a list drawn inside the
 * calendar (over the days), so it can never run past the calendar like the browser's own select list does.
 * Only the years and months the picker accepts are offered. Use it as `renderHeader` together with
 * `calendarClassName='calendar-with-panels'` (the skin anchors the list to the calendar body).
 */
function CalendarHeader({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  previousMonthDisabled,
  nextMonthDisabled,
  minDate,
  maxDate,
  locale
}: Props) {
  const [panel, setPanel] = useState<Panel | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const openerRef = useRef<HTMLButtonElement | null>(null)

  const year = date.getFullYear()
  const month = date.getMonth()
  const minIndex = minDate.getFullYear() * 12 + minDate.getMonth()
  const maxIndex = maxDate.getFullYear() * 12 + maxDate.getMonth()

  const years = useMemo(() => {
    const first = Math.floor(minIndex / 12)
    const last = Math.floor(maxIndex / 12)
    return Array.from({ length: Math.max(last - first + 1, 0) }, (_, i) => last - i)
  }, [minIndex, maxIndex])

  const monthNames = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, { month: 'long' })
    return Array.from({ length: 12 }, (_, m) => format.format(new Date(2000, m, 1)))
  }, [locale])

  function isMonthAllowed(y: number, m: number) {
    const index = y * 12 + m
    return index >= minIndex && index <= maxIndex
  }

  // Choosing a year can land on a month the picker does not accept (December of a year that ends in October):
  // move to the nearest accepted month instead of showing a calendar with every day disabled.
  useEffect(() => {
    const index = year * 12 + month
    if (index < minIndex || index > maxIndex) {
      const inRange = year >= Math.floor(minIndex / 12) && year <= Math.floor(maxIndex / 12)
      if (inRange) changeMonth(((Math.min(Math.max(index, minIndex), maxIndex) % 12) + 12) % 12)
    }
  }, [year, month, minIndex, maxIndex, changeMonth])

  // When a list opens, bring the selected item into view and give it the focus
  useEffect(() => {
    const list = listRef.current
    if (!panel || !list) return
    const selected = list.querySelector<HTMLElement>('[aria-selected="true"]')
    if (selected) {
      list.scrollTop = selected.offsetTop - (list.clientHeight - selected.offsetHeight) / 2
      selected.focus({ preventScroll: true })
    }
  }, [panel])

  function togglePanel(next: Panel, button: HTMLButtonElement) {
    openerRef.current = button
    setPanel((current) => (current === next ? null : next))
  }

  function closePanel() {
    setPanel(null)
    openerRef.current?.focus()
  }

  function handlePanelKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      // the calendar closes on Escape as well; here it only closes the list
      event.stopPropagation()
      event.preventDefault()
      closePanel()
    }
  }

  function pickYear(value: number) {
    changeYear(value)
    closePanel()
  }

  function pickMonth(value: number) {
    changeMonth(value)
    closePanel()
  }

  const yearTitle = tr('FRONTEND.STATEMENT.EXPORT.YEAR_LABEL', 'Year')
  const monthTitle = tr('FRONTEND.STATEMENT.EXPORT.MONTH_LABEL', 'Month')

  return (
    <div className={cn(styles['calendar-header'], 'calendar-header')} data-test='calendar-header'>
      <button
        type='button'
        className={cn(styles['calendar-header__arrow'], 'calendar-header__arrow')}
        onClick={decreaseMonth}
        disabled={previousMonthDisabled}
        aria-label={tr('FRONTEND.CALENDAR.PREVIOUS_MONTH', 'Previous month')}
        data-test='calendar-previous-month'
      >
        ‹
      </button>

      <div className={cn(styles['calendar-header__selects'], 'calendar-header__selects')}>
        <button
          type='button'
          className={cn(styles['calendar-header__button'], 'calendar-header__button')}
          onClick={(event) => togglePanel('month', event.currentTarget)}
          aria-haspopup='listbox'
          aria-expanded={panel === 'month'}
          aria-label={monthTitle}
          data-test='calendar-month-button'
        >
          {monthNames[month]}
          <DropdownChevron expanded={panel === 'month'} />
        </button>
        <button
          type='button'
          className={cn(styles['calendar-header__button'], 'calendar-header__button')}
          onClick={(event) => togglePanel('year', event.currentTarget)}
          aria-haspopup='listbox'
          aria-expanded={panel === 'year'}
          aria-label={yearTitle}
          data-test='calendar-year-button'
        >
          {year}
          <DropdownChevron expanded={panel === 'year'} />
        </button>
      </div>

      <button
        type='button'
        className={cn(styles['calendar-header__arrow'], 'calendar-header__arrow')}
        onClick={increaseMonth}
        disabled={nextMonthDisabled}
        aria-label={tr('FRONTEND.CALENDAR.NEXT_MONTH', 'Next month')}
        data-test='calendar-next-month'
      >
        ›
      </button>

      {panel && (
        <div
          className={cn(styles['calendar-header__panel'], 'calendar-header__panel')}
          onKeyDown={handlePanelKeyDown}
          data-test={`calendar-${panel}-panel`}
        >
          <div className={cn(styles['calendar-header__panel-bar'], 'calendar-header__panel-bar')}>
            <span>{panel === 'year' ? yearTitle : monthTitle}</span>
            <button
              type='button'
              className={cn(styles['calendar-header__close'], 'calendar-header__close')}
              onClick={closePanel}
              aria-label={tr('FRONTEND.BUTTONS.CLOSE', 'Close')}
              data-test='calendar-panel-close'
            >
              ×
            </button>
          </div>
          <div
            ref={listRef}
            role='listbox'
            aria-label={panel === 'year' ? yearTitle : monthTitle}
            className={cn(styles['calendar-header__list'], 'calendar-header__list')}
          >
            {panel === 'year'
              ? years.map((value) => (
                  <button
                    key={value}
                    type='button'
                    role='option'
                    aria-selected={value === year}
                    className={cn(styles['calendar-header__option'], 'calendar-header__option')}
                    onClick={() => pickYear(value)}
                    data-test='calendar-year-option'
                  >
                    {value}
                  </button>
                ))
              : monthNames.map((name, value) => (
                  <button
                    key={name}
                    type='button'
                    role='option'
                    aria-selected={value === month}
                    disabled={!isMonthAllowed(year, value)}
                    className={cn(styles['calendar-header__option'], 'calendar-header__option')}
                    onClick={() => pickMonth(value)}
                    data-test='calendar-month-option'
                  >
                    {name}
                  </button>
                ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default CalendarHeader
